import { useRef } from "react";
import {
  Box, Flex, HStack, Button,
  Table, Thead, Tbody, Tr, Th, Td,
  Text, Badge, IconButton, Icon,
  AlertDialog, AlertDialogBody, AlertDialogFooter,
  AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
  useColorModeValue,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, AddIcon, ViewIcon } from "@chakra-ui/icons";
import { useRolesAdmin } from "./useRolesAdmin";
import { RoleFormModal } from "./RoleFormModal";
import { RolePermissionsDrawer } from "./RolePermissionsDrawer";
import { ChildrenInteractionBlocker } from "../../../components/overlays/ChildrenInteractionBlocker";
import { PERMISSIONS } from "../../../config/permissions";
import { ErrorState } from "../../../components/ErrorState";
import { ListPagination } from "../../rh/onboarding/components/ListPagination";
import { OnflyLoading } from "../../../tools/OnflyLoading";

export function RolesAdminPage() {
  const cancelRef = useRef<HTMLButtonElement>(null);

  const titleColor  = useColorModeValue("#434347", "#e8edf2");
  const descColor   = useColorModeValue("#696a71", "#8fa3b8");
  const subtleColor = useColorModeValue("#84858c", "#718096");
  const thColor     = useColorModeValue("#84858c", "#718096");
  const cardBg      = useColorModeValue("white", "#0f2040");
  const cardBorder  = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const theadBg     = useColorModeValue("rgba(0,158,251,0.04)", "rgba(0,158,251,0.06)");
  const rowHoverBg  = useColorModeValue("rgba(0,158,251,0.025)", "rgba(0,158,251,0.04)");

  const {
    roles,
    allPermissions,
    loading,
    error,
    page,
    totalPages,
    setPage,
    isFormOpen,
    setIsFormOpen,
    isDrawerOpen,
    setIsDrawerOpen,
    selectedRole,
    deleteTargetId,
    handleCreateOpen,
    handleEditOpen,
    handleViewPermissionsOpen,
    handleDeleteClick,
    confirmDelete,
    cancelDelete,
    refreshList,
  } = useRolesAdmin();

  return (
    <Box p={{ base: 4, md: 8 }}>
      <Flex justify="space-between" align="flex-start" mb={6} gap={4} wrap="wrap">
        <Box>
          <Text fontSize="2xl" fontWeight={700} color={titleColor} mb={1}>
            Gerenciamento de Papéis
          </Text>
          <Text fontSize="sm" color={descColor}>
            Configure os papéis de acesso e suas permissões associadas.
          </Text>
        </Box>
        <ChildrenInteractionBlocker requiredPermissions={[PERMISSIONS.ADMIN.MANAGE_ROLES]}>
          <Button
            leftIcon={<Icon as={AddIcon} />}
            bg="#009efb"
            color="white"
            _hover={{ bg: "#007bd4" }}
            borderRadius="50px"
            h="40px"
            px={5}
            fontSize="14px"
            fontWeight={500}
            onClick={handleCreateOpen}
            flexShrink={0}
          >
            Novo Papel
          </Button>
        </ChildrenInteractionBlocker>
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" py={16}>
          <OnflyLoading containerSize={80} />
        </Flex>
      ) : error ? (
        <ErrorState onRetry={refreshList} />
      ) : roles.length === 0 ? (
        <Text color={subtleColor} fontSize="sm">Nenhum papel cadastrado.</Text>
      ) : (
        <Box
          bg={cardBg}
          borderRadius="12px"
          borderWidth="1px"
          borderColor={cardBorder}
          boxShadow="0px 4px 8px 0px rgba(14, 61, 93, 0.08)"
          overflow="hidden"
          overflowX="auto"
        >
          <Table variant="simple" minW="480px">
            <Thead bg={theadBg}>
              <Tr>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>Nome</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>Descrição</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" py={3}>Permissões</Th>
                <Th color={thColor} fontSize="11px" letterSpacing="0.5px" textTransform="none" py={3}>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {roles.map((role) => (
                <Tr key={role.id} _hover={{ bg: rowHoverBg }} transition="background 0.12s">
                  <Td borderColor={cardBorder} py={3.5}>
                    <Text fontWeight={600} fontSize="sm" color={titleColor}>{role.name}</Text>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <Text fontSize="sm" color={descColor}>{role.description || "—"}</Text>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <HStack spacing={2}>
                      <IconButton
                        aria-label="Ver permissões"
                        icon={<ViewIcon />}
                        size="sm"
                        variant="ghost"
                        color="#009efb"
                        _hover={{ bg: "rgba(0,158,251,0.08)" }}
                        onClick={() => handleViewPermissionsOpen(role)}
                      />
                      <Badge
                        borderRadius="50px"
                        bg="rgba(0,158,251,0.12)"
                        color="#009efb"
                        fontSize="11px"
                        fontWeight={600}
                        px={2.5}
                        py={0.5}
                      >
                        {role.permissions.length}
                      </Badge>
                    </HStack>
                  </Td>
                  <Td borderColor={cardBorder} py={3.5}>
                    <HStack spacing={0}>
                      <ChildrenInteractionBlocker requiredPermissions={[PERMISSIONS.ADMIN.MANAGE_ROLES]}>
                        <IconButton
                          aria-label="Editar papel"
                          icon={<EditIcon />}
                          size="sm"
                          variant="ghost"
                          color="#009efb"
                          _hover={{ bg: "rgba(0,158,251,0.08)" }}
                          onClick={() => handleEditOpen(role)}
                        />
                      </ChildrenInteractionBlocker>
                      <ChildrenInteractionBlocker requiredPermissions={[PERMISSIONS.ADMIN.MANAGE_ROLES]}>
                        <IconButton
                          aria-label="Excluir papel"
                          icon={<DeleteIcon />}
                          size="sm"
                          variant="ghost"
                          color={subtleColor}
                          _hover={{ bg: "rgba(220,53,69,0.08)", color: "#dc3545" }}
                          onClick={() => handleDeleteClick(role.id)}
                        />
                      </ChildrenInteractionBlocker>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      <ListPagination page={page} totalPages={totalPages} onPageChange={setPage} />

      <RoleFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        role={selectedRole}
        allPermissions={allPermissions}
        onSuccess={refreshList}
      />

      <RolePermissionsDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        role={selectedRole}
      />

      <AlertDialog isOpen={deleteTargetId !== null} leastDestructiveRef={cancelRef} onClose={cancelDelete}>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="2xl">
            <AlertDialogHeader fontSize="md" fontWeight={600}>Confirmar exclusão</AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              Tem certeza que deseja excluir este papel? Esta ação não pode ser desfeita.
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <Button ref={cancelRef} variant="ghost" size="sm" borderRadius="50px" onClick={cancelDelete}>
                Cancelar
              </Button>
              <Button colorScheme="red" size="sm" borderRadius="50px" onClick={confirmDelete}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
